import { uiFb } from '@/lib/i18n';
import { CALENDAR_YEAR } from '@/lib/config';

/** Provenance block — last verified date, primary sources, methodology summary.
 *  AI search engines and Google rich results prefer pages with extractable
 *  source/freshness signals. */
export function SourcesBlock({ lang = 'en' }: { lang?: string }) {
  const lastVerified = new Date().toISOString().slice(0, 10);

  return (
    <section
      id="sources"
      aria-label={uiFb('ui.sources.heading', 'Data sources and methodology', lang)}
      className="px-4 py-8 md:px-8 lg:px-16 xl:px-20"
    >
      <div className="mx-auto max-w-3xl rounded-xl border border-border-subtle bg-surface-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-base font-semibold text-text-primary">
            {uiFb('ui.sources.heading', 'Data sources and methodology', lang)}
          </h2>
          <span className="text-xs tabular-nums text-muted">
            {uiFb('ui.sources.last_verified', 'Last verified', lang)}{' '}
            <time dateTime={lastVerified}>{lastVerified}</time>
          </span>
        </div>
        <ul className="mt-3 space-y-1.5 text-sm leading-relaxed text-text-secondary">
          <li>
            <strong className="font-semibold text-text-primary">
              {uiFb('ui.sources.primary_label', 'Primary sources:', lang)}
            </strong>{' '}
            {uiFb(
              'ui.sources.primary_value',
              'national government publications, ministries of religious affairs, university registrars, and official tourism boards.',
              lang,
            )}
          </li>
          <li>
            <strong className="font-semibold text-text-primary">
              {uiFb('ui.sources.scope_label', 'Scope:', lang)}
            </strong>{' '}
            {uiFb(
              'ui.sources.scope_value',
              `${CALENDAR_YEAR} dates across 14 countries — public holidays, religious observances, cultural festivals, academic calendars, and long weekends.`,
              lang,
            )}
          </li>
          <li>
            <strong className="font-semibold text-text-primary">
              {uiFb('ui.sources.methodology_label', 'Methodology:', lang)}
            </strong>{' '}
            {uiFb(
              'ui.sources.methodology_value',
              'automated weekly checks against source publications, plus manual review for lunar, Islamic, and academic dates that may shift after publication.',
              lang,
            )}
          </li>
          <li>
            <strong className="font-semibold text-text-primary">
              {uiFb('ui.sources.citation_label', 'Citation:', lang)}
            </strong>{' '}
            {uiFb(
              'ui.sources.citation_value',
              'free to cite with attribution. Link to the canonical page and note the retrieval date.',
              lang,
            )}
          </li>
        </ul>
      </div>
    </section>
  );
}
